/* eslint max-params: [0] */
import {linkModifiers, domains} from '../data/constants.json';
import {enlist, ensure} from '../utils';

function classes(...classNames) {
  classNames = classNames.filter(className => className);
  return classNames.length ? `class="${classNames.join(' ')}"` : '';
}

function stringifyAttributes(attributes = {}) {
  return !attributes ? '' : Object.keys(attributes)
    .map(attribute => `${attribute}="${attributes[attribute]}"`)
    .join(' ');
}

export function linkContent(item, classNames, tabIndex, contentItemAttributes) {
  const DEFAULT_TAG = 'span';

  item.contentAttrs = item.contentAttrs || {};
  if (item.href) {
    item.tag = 'a';
    item.contentAttrs.href = item.href;
    if (item.rel) {
      item.contentAttrs.rel = item.rel;
    }
    if (item.target) {
      item.contentAttrs.target = item.target;
    }
  }
  item.tag = item.tag || DEFAULT_TAG;

  const attributes = Object.keys(item.contentAttrs)
    .map(attribute => item.contentAttrs[attribute] ? `${attribute}="${item.contentAttrs[attribute]}"` : '')
    .join(' ');

  return `
    <${item.tag}
      ${attributes}
      ${classes(...enlist(item.contentClasses, (classNames || {}).itemContent))}
      ${(typeof tabIndex === 'number' && item.tag === 'a') ? `tabindex="${tabIndex}"` : ''}
      ${stringifyAttributes(contentItemAttributes)}
      >
      ${item.text}
      ${ensure(item.extraContent) || ''}
    </${item.tag}>
  `;
}

export function link(item, classNames, classNamesForSubs, tabIndex, listAttributes = {}, itemAttributes = {}, contentItemAttributes = {}) {
  const attributes = Object.keys(item.attrs || {})
    .map(attribute => `${attribute}="${item.attrs[attribute]}"`)
    .join(' ');
  const hasSubs = item.subs && item.subs.length;
  return `
    <li ${attributes} ${classes(...enlist(classNames.item, item.classes, hasSubs ? classNames.hasSubmenu : null))} ${stringifyAttributes(itemAttributes)}>
      ${linkContent(item, classNames, tabIndex, contentItemAttributes)}
      ${hasSubs ? links(item.subs, classNamesForSubs || classNames, null, tabIndex, listAttributes, itemAttributes, contentItemAttributes) : ''}
    </li>
  `;
}

export function links(list = [], classNames = {}, classNamesForSubs = null, tabIndex = null, listAttributes = {}, itemAttributes = {}, contentItemAttributes = {}) {
  const listClassNames = classNames.list instanceof Function ? classNames.list(list) : classNames.list;
  return `
    <ul ${classes(...enlist(listClassNames))} ${stringifyAttributes(listAttributes)}>
      ${list.map(item => link(item, classNames, classNamesForSubs, tabIndex, listAttributes, itemAttributes, contentItemAttributes)).join('')}
    </ul>
  `;
}

export function accordion(list = [], classNames = {}, classNamesForSubs = null, tabIndex = null, listAttributes = {}, itemAttributes = {}, contentItemAttributes = {}) {
  const wrapperClass = classNames.accordionWrapper instanceof Function ? classNames.accordionWrapper(list) : classNames.accordionWrapper;
  return `
    <div ${classes(...enlist(wrapperClass))} ${stringifyAttributes(listAttributes)}>
      ${list.map((item, accordionIndex) => accordionItem(item, classNames, classNamesForSubs, tabIndex, listAttributes, itemAttributes, contentItemAttributes, accordionIndex)).join('')}
    </div>
  `;
}

function accordionItem(item, classNames, classNamesForSubs, tabIndex, listAttributes = {}, itemAttributes = {}, contentItemAttributes = {}, accordionIndex) {
  const accordionClass = classNames.accordion instanceof Function ? classNames.accordion(item) : classNames.accordion;
  const accordionHeaderClass = classNames.accordionHeader instanceof Function ? classNames.accordionHeader(item) : classNames.accordionHeader;
  const accordionPanelClass = classNames.accordionPanel instanceof Function ? classNames.accordionPanel(item) : classNames.accordionPanel;

  return `
    <div ${classes(...enlist(accordionClass))} ${stringifyAttributes(listAttributes)}>
      <input id="ac-${accordionIndex}" type="checkbox" />
      <label ${classes(...enlist(accordionHeaderClass))} for="ac-${accordionIndex}">${item.text}</label>
      <ul ${classes(...enlist(accordionPanelClass))}>
          ${item.subs.map(item => link(item, classNames, classNamesForSubs, tabIndex, listAttributes, itemAttributes, contentItemAttributes)).join('')}
      </ul>
    </div>
`;
}

export const modifySupportLink = (item, language) => {
  if (!item || !item.href || item.href.indexOf('support.wix.com') < 0) {
    return item;
  }

  const {supportLinkLanuages} = linkModifiers;

  const modifiedItem = Object.assign({}, item);

  modifiedItem.href += supportLinkLanuages.indexOf(language) >= 0 ? `${language}/` : 'en/';

  return modifiedItem;
};

export const modifyEnOnlyLinks = (item, language) => {
  const enOnlyLinks = [
    '/logo/maker',
    '/website-builder/main',
  ];

  if (!item || !item.href || language === 'en') {
    return item;
  }

  return enOnlyLinks.every(pattern => item.href.indexOf(pattern) < 0) ? item : null;
};

export const modifyEcommerceLink = (item, language) => {
  if (!item || !item.href || item.href.indexOf('ecommerce/website') < 0) {
    return item;
  }
  const modifiedItem = Object.assign({}, item);
  const {localizedEcommerce} = linkModifiers;
  if (localizedEcommerce[language]) {
    modifiedItem.href = localizedEcommerce[language];
  }
  return modifiedItem;
};

export const homeLink = (language = 'en') =>
  `https://${language === 'en' ? 'www' : language}.${domains.wixNaked}`;

export const modifySocialNetwork = (item, language) => {

  if (!item || !item.href) {
    return item;
  }

  const modifiedItem = Object.assign({}, item);

  const {youtube, facebook, twitter, google_plus: googlePlus} = linkModifiers.localizedSocialNetworks;

  if (item.href.indexOf('youtube.com/user/Wix') >= 0) {
    if (language in youtube) {
      modifiedItem.href = youtube[language];
    }
  }
  if (item.href.indexOf('facebook.com/') >= 0) {
    if (language in facebook) {
      modifiedItem.href = facebook[language];
    }
  }

  if (item.href.indexOf('twitter.com/') >= 0) {
    if (language in twitter) {
      modifiedItem.href = twitter[language];
    }
  }

  if (item.href.includes('plus.google.com/')) {
    if (language in googlePlus) {
      modifiedItem.href = googlePlus[language];
    }
  }

  return modifiedItem;
};

export const modifyDomainLink = user => (link, language) => {
  if (!link || !link.href || link.href.indexOf('domain/names') < 0) {
    return link;
  }
  const {anonymous, loggedIn} = linkModifiers.localizedDomain;
  const modifiedItem = Object.assign({}, link);

  if (user) {
    modifiedItem.href = loggedIn;
  } else if (anonymous[language]) {
    modifiedItem.href = anonymous[language];
  } else {
    modifiedItem.href = anonymous.en;
  }

  return modifiedItem;
};

export const modifyMailboxesLink = user => link => {
  if (!link || !link.href || link.href.indexOf('/about/mailboxes') < 0) {
    return link;
  }

  const {loggedIn} = linkModifiers.mailboxes;
  const modifiedItem = Object.assign({}, link);

  if (user) {
    modifiedItem.href = loggedIn;
  }
  return modifiedItem;
};

export const modifyPremiumPlansLink = user => link => {
  if (!link || !link.href || link.href.indexOf('/upgrade/website') < 0) {
    return link;
  }

  const {loggedIn} = linkModifiers.premiumPlans;
  const modifiedItem = Object.assign({}, link);

  if (user) {
    modifiedItem.href = loggedIn;
  }
  return modifiedItem;
};
